import {
  DestroyRef,
  Injectable,
  inject,
  WritableSignal,
  signal,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import {Constants} from '../../../../../common/constants/constants';

@Injectable({
  providedIn: 'root',
})
export class PreviousRouteService {

  private router: Router = inject(Router);

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private previousUrl: WritableSignal<string> = signal<string>(null);

  private currentUrl: WritableSignal<string> = signal<string>(null);

  private stateUrls: WritableSignal<string[]> = signal<string[]>([]);

  constructor() {
    this.currentUrl.set(this.router.url);
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.previousUrl.set(this.currentUrl());
        this.currentUrl.set(event.url);
      }
    });
  }

  /**
   * Method: Init router navigation event
   * @param event
   */
  init(event: any): void {
    if (event instanceof NavigationStart) {
      this.setStateUrl(event.url);
    }
  }

  /**
   * Method: Get previous url
   */
  public getPreviousUrl(): string {
    return this.previousUrl();
  }

  /**
   * Method: Get current url
   */
  public getCurrentUrl(): string {
    return this.currentUrl();
  }

  /**
   * Method: Set state url
   */
  public setStateUrl(stateUrl: string): void {
    const url: string = this.removeFlwHashing(stateUrl);
    if (!url || this.findStateUrl(url)) {
      return;
    }
    this.stateUrls.update(state => [...state, url]);
  }

  /**
   * Method: Find state url
   */
  public findStateUrl(stateUrl: string): boolean {
    const url: string = this.removeFlwHashing(stateUrl);
    return this.stateUrls().includes(url);
  }

  /**
   * Method: Get state url by index
   * @param index
   */
  public getStateUrl(index: number): string {
    return this.stateUrls()[index];
  }

  /**
   * Method: Get previous state url
   */
  public getStateUrlPrevious(): string {
    const prevIndex = this.getStateUrls().length - 2;
    if (prevIndex < 0) {
      return null;
    }
    return this.getStateUrl(prevIndex);
  }

  /**
   * Method: Remove state url
   * @param stateUrl
   */
  public removeStateUrl(stateUrl: string): void {
    this.stateUrls.update(state => state.filter(url => url !== stateUrl));
  }

  /**
   * Method: Get state urls
   */
  public getStateUrls(): string[] {
    return this.stateUrls();
  }

  /**
   * Method: Clear state urls
   */
  public clearStateUrls(): void {
    this.stateUrls.set([]);
  }

  /**
   * Method: Update state urls after previous redirect
   */
  public updateStateUrlsAfterPreviousRedirect(): void {
    this.stateUrls.update(state => state.slice(0, -1));
  }

  /**
   * Method: Check if state urls exists
   */
  public hasStateUrls(): boolean {
    return this.getStateUrls().length > 0;
  }

  /**
   * Method: Check if any state url contains backFlwUrl query parameter
   */
  public hasBackFlwUrlInHistory(): boolean {
    return this.getStateUrls().some(url => url.includes(Constants.BACK_FLW_URL_PARAM_KEY));
  }

  /**
   * Method: Remove flowable hashing
   * @param url
   * @private
   */
  private removeFlwHashing(url: string): string {
    return url.replace(Constants.HASHTAG + Constants.SLASH, '');
  }

}
